import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Eye, Key, MoreVertical, Download, MapPin, AlertCircle, Globe, Activity, Clock, Target, EyeOff } from 'lucide-react';
import { useAdmin, UserDetails } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const UserManagement = () => {
  const { isAdmin, fetchUsers, resetUserPassword } = useAdmin();
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const userData = await fetchUsers(1, 100);
      setUsers(userData);
      if (userData.length === 0) {
        toast.info('No user data found. Try refreshing the analytics data first.');
      }
    } catch (error: any) {
      console.error('Failed to load users:', error);
      let errorMessage = 'Failed to load user data';
      if (error.message?.includes('access_denied')) {
        errorMessage = 'Access denied: Only super admins can view user analytics';
      } else if (error.message?.includes('ambiguous')) {
        errorMessage = 'Database error: Analytics function needs to be updated';
      } else if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        errorMessage = 'System error: Required analytics functions are missing';
      } else if (error.message?.includes('JWT')) {
        errorMessage = 'Authentication error: Please refresh your session and try again';
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMessage = 'Network error: Please check your connection and try again';
      } else {
        errorMessage = `Failed to load users: ${error.message || 'Unknown error'}`;
      }
      toast.error(errorMessage);
      // Set empty array so the UI doesn't break
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!selectedUser || !newPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }

    setResettingPassword(true);
    try {
      const result = await resetUserPassword(selectedUser.id, newPassword);
      if (result.success) {
        toast.success('Password reset successfully');
        setShowPasswordReset(false);
        setNewPassword('');
        setSelectedUser(null);
      } else {
        toast.error(result.error || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    }
    setResettingPassword(false);
  };

  const exportUsers = () => {
    const csvContent = [
      ['Username', 'Email', 'ELO Rating', 'Games Played', 'Subscription', 'Status', 'Last IP', 'Last Country', 'Last City', 'Last Login', 'Login Count', 'Unique Countries', 'Total Activities', 'Puzzles Attempted', 'Most Used Feature', 'Activity Score', 'Created At'].join(','),
      ...filteredUsers.map(user => [
        user.username,
        user.email,
        user.elo_rating,
        user.games_played,
        user.subscription_type,
        user.subscription_status,
        user.last_ip || '',
        user.last_country || '',
        user.last_city || '',
        user.last_login ? new Date(user.last_login).toLocaleDateString() : '',
        user.login_count || 0,
        user.unique_countries || 0,
        user.total_activities || 0,
        user.puzzles_attempted || 0,
        user.most_used_feature || '',
        user.activity_score || 0,
        new Date(user.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chesshatch-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">User Management</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-muted rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage and monitor user accounts</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportUsers} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button onClick={loadUsers}>Refresh</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Users ({filteredUsers.length})</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Game Stats</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary font-medium">
                          {user.full_name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase() || 
                           user.display_name?.charAt(0)?.toUpperCase() || 
                           user.username?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-foreground">
                          {user.full_name || user.display_name || user.username || 'Unknown User'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          @{user.username}
                        </div>
                        {user.full_name && user.full_name !== user.display_name && (
                          <div className="text-xs text-muted-foreground">
                            Display: {user.display_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{user.email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">ELO:</span> {user.elo_rating}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {user.games_played} games • {user.games_won}W-{user.games_lost}L-{user.games_drawn}D
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm">
                        <Activity className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium">{user.total_activities || 0}</span>
                        <span className="text-muted-foreground">activities</span>
                        <Badge variant={
                          (user.activity_score || 0) >= 4 ? "default" : 
                          (user.activity_score || 0) >= 2 ? "secondary" : "outline"
                        } className="ml-1">
                          {user.activity_score || 0}/5
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <Target className="w-3 h-3 inline mr-1" />
                        {user.puzzles_attempted || 0} puzzles • {user.tutorials_accessed || 0} tutorials
                      </div>
                      {user.most_used_feature && (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">Top:</span> {user.most_used_feature}
                        </div>
                      )}
                      {user.total_time_spent_hours && user.total_time_spent_hours > 0 && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {user.total_time_spent_hours}h total
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {user.last_country && user.last_country !== 'Hidden' ? (
                        <div className="flex items-center gap-1 text-sm flex-wrap">
                          <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          <span className={user.last_country === 'Hidden' ? 'italic text-muted-foreground' : 'font-medium'}>
                            {user.last_city && user.last_city !== 'Unknown' ? `${user.last_city}, ${user.last_country}` : user.last_country}
                          </span>
                          {user.is_suspicious_login && (
                            <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                          )}
                          {(user as any).location_sharing_consent === false && (
                            <Badge variant="outline" className="text-xs ml-1">
                              <EyeOff className="w-3 h-3 mr-1" />
                              Private
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground italic flex items-center gap-1">
                          <EyeOff className="w-3 h-3" />
                          {user.last_country === 'Hidden' ? 'Hidden, Unknown' : 'No location data'}
                        </div>
                      )}
                      {user.last_ip && (
                        <div className="text-xs text-muted-foreground font-mono">{user.last_ip}</div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {user.login_count || 0} logins • {user.unique_countries || 0} countries
                      </div>
                      {user.last_login && (
                        <div className="text-xs text-muted-foreground">
                          Login: {new Date(user.last_login).toLocaleDateString()}
                        </div>
                      )}
                      {user.last_activity && (
                        <div className="text-xs text-muted-foreground">
                          Activity: {new Date(user.last_activity).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Badge variant={user.subscription_type === 'free' ? 'secondary' : 'default'}>
                        {user.subscription_type}
                      </Badge>
                      <div className="text-xs text-muted-foreground">
                        {user.subscription_status}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {user.last_used_date 
                          ? new Date(user.last_used_date).toLocaleDateString()
                          : new Date(user.created_at).toLocaleDateString()
                        }
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {user.last_used_date && user.last_used_date !== user.created_at
                          ? `Active ${Math.floor((Date.now() - new Date(user.last_used_date).getTime()) / (1000 * 60 * 60 * 24))} days ago`
                          : `Joined ${new Date(user.created_at).toLocaleDateString()}`
                        }
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setShowPasswordReset(true);
                          }}
                        >
                          <Key className="mr-2 h-4 w-4" />
                          Reset Password
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showPasswordReset} onOpenChange={setShowPasswordReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User</Label>
              <div className="mt-1 p-2 bg-muted rounded text-sm">
                {selectedUser?.username} ({selectedUser?.email})
              </div>
            </div>
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordReset(false);
                  setNewPassword('');
                  setSelectedUser(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordReset}
                disabled={resettingPassword || !newPassword.trim()}
              >
                {resettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};